// Landing-origin + geo defaults for SelectResumeFormatDialog (Tailor Your Resume).

import {
  TEMPORARY_SIGNUP_REDIRECT_PATH,
  type TemporarySignupAction,
} from '@/features/marketing/lib/marketing-constants';
import { parseStrictTargetFormat } from '@/features/portfoliosettings/lib/editor-deep-links';
import { getSessionStorageItem, setSessionStorageItem } from '@/lib/storage/web-storage';

import type { ResumeTargetFormat } from '../types';

export const LANDING_ORIGIN_STORAGE_KEY = 'kw_landing_origin';

export function formatFromLandingPath(
  pathname: string | null | undefined,
): ResumeTargetFormat | null {
  if (!pathname) {
    return null;
  }
  const path = pathname.toLowerCase();
  if (path.includes('europass-cv-builder')) {
    return 'europass';
  }
  if (path.includes('uae-cv-builder')) {
    return 'uae';
  }
  if (path.includes('germany-cv-builder')) {
    return 'germany';
  }
  if (path.includes('resume-format-checker/germany')) {
    return 'germany';
  }
  return null;
}

export function geoCountryFromProfile(
  profile:
    | {
        request_ip_country?: string | null;
        ip_country?: string | null;
        requestIpCountry?: string | null;
        ipCountry?: string | null;
      }
    | null
    | undefined,
): string | null {
  if (!profile) {
    return null;
  }
  return (
    profile.request_ip_country ||
    profile.ip_country ||
    profile.requestIpCountry ||
    profile.ipCountry ||
    null
  );
}

const GEO_FORMAT_BY_COUNTRY: Record<string, ResumeTargetFormat> = {
  IN: 'india',
  US: 'usa',
  AE: 'uae',
  DE: 'germany',
};

export function formatFromGeoCountry(
  country: string | null | undefined,
): ResumeTargetFormat | null {
  if (!country) {
    return null;
  }
  const mapped = GEO_FORMAT_BY_COUNTRY[String(country).trim().toUpperCase()];
  return mapped || null;
}

export function plansIndicateIndia(
  plans: Array<{ currency?: string; currency_code?: string }> | null | undefined,
): boolean {
  if (!plans || plans.length === 0) {
    return false;
  }
  const first = plans[0];
  if (!first) {
    return false;
  }
  return first.currency === 'INR' || first.currency_code === 'INR';
}

export function persistLandingOrigin(pathname: string): void {
  setSessionStorageItem(LANDING_ORIGIN_STORAGE_KEY, pathname);
}

export function readLandingOrigin(): string | null {
  return getSessionStorageItem(LANDING_ORIGIN_STORAGE_KEY);
}

export type ResolveTailorInitialFormatInput = {
  resumeFormat?: string | null;
  queryFormat?: string | null;
  landingPath?: string | null;
  geoCountry?: string | null;
  plansIndicateIndia?: boolean;
};

function resolveLandingPath(landingPath: string | null | undefined): string | null {
  if (landingPath !== undefined) {
    return landingPath;
  }
  return readLandingOrigin();
}

/** Schema default is universal — treat it as unset so landing/geo can preselect. */
export function isExplicitResumeFormat(value: string | null | undefined): boolean {
  const parsed = parseStrictTargetFormat(value);
  return parsed != null && parsed !== 'universal';
}

/**
 * Priority: query/landing origin > explicit resume format > IN/US/AE/DE geo > INR → India >
 * International.
 */
export function resolveTailorInitialFormat(
  input: ResolveTailorInitialFormatInput,
): ResumeTargetFormat {
  const query = parseStrictTargetFormat(input.queryFormat);
  if (query) {
    return query;
  }
  const fromPath = formatFromLandingPath(resolveLandingPath(input.landingPath));
  if (fromPath) {
    return fromPath;
  }
  const resume = parseStrictTargetFormat(input.resumeFormat);
  if (resume && resume !== 'universal') {
    return resume;
  }
  const geo = formatFromGeoCountry(input.geoCountry);
  if (geo) {
    return geo;
  }
  if (input.plansIndicateIndia) {
    return 'india';
  }
  return 'universal';
}

export function needsPublicPlansIndiaFallback(
  input: Omit<ResolveTailorInitialFormatInput, 'plansIndicateIndia'>,
): boolean {
  return (
    !parseStrictTargetFormat(input.queryFormat) &&
    !formatFromLandingPath(resolveLandingPath(input.landingPath)) &&
    !isExplicitResumeFormat(input.resumeFormat) &&
    !formatFromGeoCountry(input.geoCountry)
  );
}

export function buildTemporaryEditorRedirect(
  webDomain: string,
  action: TemporarySignupAction,
  pathname?: string,
): string {
  const base = `${webDomain}${TEMPORARY_SIGNUP_REDIRECT_PATH}?mode=editor&action=${action}`;
  if (action !== 'import') {
    return base;
  }
  const format = formatFromLandingPath(pathname);
  if (!format) {
    return base;
  }
  return `${base}&format=${encodeURIComponent(format)}`;
}
